import React, { useEffect, useState, useCallback, useMemo } from "react";
import {Accordion, Alert, Button, Col, Row} from "react-bootstrap";
import { useSubheader } from "@metronic/layout";
import { FamilyData } from "@components/panels/family-data/family-data";
import { HospitalData } from "@components/panels/hospital-data/hospital-data";
import { InssuredData } from "@components/panels/insured-data/inssured-data";
import { SufferingData } from "@components/panels/suffering-data/suffering-data";
import { Documentation } from "@components/panels/documentation/documentation";
import { Binnacle } from "@components/panels/binnacle/binnacle";
import { MedicAssigned } from "@components/panels/medic-assigned/medic-assigned";
import { update, getBySlug } from "@services/hospitalization";
import {useHistory, useLocation} from "react-router-dom";
import axios from "axios";
import { isEmpty } from "@utils/check-object-empty";
import { toBooleanOrNull } from "@utils/generic-functions";
import moment from "moment";
import "moment-timezone";
import BlockCaseContext from "@contexts/block-case-context";
import { useCommentValidation } from "@contexts/comment-validation-context";
import WithCommentValidation from "@components/shared/with-comment-validation";
import {CASE_STATUS} from "@constants/case-statuses";
import {INCOME_TYPE} from "@constants/income-types";
import AuthLetters from "@components/panels/auth-letter/auth-letters";
import { useAutomaticCommentSave } from "@hooks/use-automatic-comment-save";
import { useMultiFormManager } from "@contexts/multi-form-manager-context";
import WithMultiFormManager from "@components/shared/with-multi-form-manager";
import { useHospitalizationQueryParams } from "@hooks/use-query-params";
import { flowRight as compose } from "lodash";
import {getMexicoTimestamp} from "@utils/date-utils";

const Hospitalization = (props) => {
  const [shouldBlockCase, setShouldBlockCase] = useState(true);
  const [caseRecord, setCaseRecord] = useState({});
  
  const history = useHistory();
  const queryParams = useHospitalizationQueryParams();
  const subheader = useSubheader();
  
  const { validateCommentRequired } = useCommentValidation();
  const { validateAllForms, getAllFormValues, getFormCount, getRegisteredFormNames } = useMultiFormManager();
  const { saveCommentAutomatically } = useAutomaticCommentSave({ caseData: caseRecord });
  const { saveCommentAutomaticallyUnam } = useAutomaticCommentSave({
    caseData: caseRecord,
    eventStage: null,
    comment: "FAVOR DE FACTURAR A UNAM, DE NO HACERLO NO SE PODRÁ PAGAR",
    isPageable: false,
    hideReason: true,
  });

  const { slug } = props.match.params;

  const getCaseBySlug = useCallback(() =>
    getBySlug(slug).then((response) => setCaseRecord(response.data)), [slug]
  );

  useEffect(() => {
    subheader.setTitle("Ingreso hospitalario");
    getCaseBySlug();
  }, [subheader, getCaseBySlug]);

  const combineFormDataWithTimestamps = useCallback((caseStatusId) => {
    const allFormValues = getAllFormValues();
    
    const combinedData = {
      ...caseRecord,
      ...allFormValues.hospitalData || {},
      ...allFormValues.inssuredData || {}, 
      ...allFormValues.familyData || {},
      ...allFormValues.medicAssigned || {},
    };

    if (combinedData['vip'] === "Si") {
      combinedData['axaCategories'] = document.getElementById('axaCategories')?.value || '';
    }

    const mexicoTimestamp = getMexicoTimestamp();

    if (caseStatusId === CASE_STATUS.CONTROL_DESK) {
      combinedData["feamesaControl"] = mexicoTimestamp;
    } else if (caseStatusId === CASE_STATUS.MEDIC_INFORMATION) {
      combinedData["feaimedica"] = mexicoTimestamp;
    }

    if (combinedData?.incomeType === INCOME_TYPE.SCHEDULED_SURGERY.value) {
      combinedData["caseStatusId"] = CASE_STATUS.MEDIC_INFORMATION;
      combinedData["feaimedica"] = mexicoTimestamp;
    } else {
      combinedData["caseStatusId"] = caseStatusId;
    }

    return combinedData;
  }, [caseRecord, getAllFormValues]);

  const onClickUpdateCase = useCallback(async (caseStatusId = CASE_STATUS.CONTROL_DESK) => {
    const validation = await validateAllForms();
    
    if (!validation.allValid || !validateCommentRequired()) {
      return;
    }

    await saveCommentAutomatically();
    await saveCommentAutomaticallyUnam();
    await update(combineFormDataWithTimestamps(caseStatusId), caseRecord.id);

    history.push("/ingreso_hospitalario");
  }, [
    validateAllForms, 
    combineFormDataWithTimestamps, 
    validateCommentRequired, 
    saveCommentAutomatically, 
    saveCommentAutomaticallyUnam,
    caseRecord, 
    history,
    getFormCount,
    getRegisteredFormNames
  ]);

  const commonFormProps = useMemo(() => ({
    icon: "fa-pencil-alt",
    data: caseRecord,
    disabledAll: false,
    enableMultiForm: true,
    bindSubmitForm: () => {}
  }), [caseRecord]);

  const buttonVisibility = useMemo(() => ({
    showControlDesk: !(queryParams.isScheduledSurgery === true && queryParams.isNewCase === false),
    showMedicInfo: queryParams.isScheduledSurgery === true && queryParams.isNewCase === false
  }), [queryParams]);

  return (
    <>
      <Alert
        variant="success"
        className="text-center"
        style={{ fontSize: "16px" }}
      >
        Ingreso Hospitalario
      </Alert>

      <p>{caseRecord.caseTicket}</p>

      <BlockCaseContext.Provider value={[shouldBlockCase, setShouldBlockCase]}>
        <Accordion defaultActiveKey="0">
          <HospitalData
            {...commonFormProps}
            eventKey="0"
            scheduledSurgery={queryParams.isScheduledSurgery}
            isNewCase={queryParams.isNewCase}
          />
          <InssuredData
            {...commonFormProps}
            eventKey="1"
          />
          <SufferingData
            {...commonFormProps}
            eventKey="2"
          />
          <FamilyData 
            {...commonFormProps}
            eventKey="3" 
          />
          <MedicAssigned
            {...commonFormProps}
            eventKey="4"
          />
          {!isEmpty(caseRecord) && (
            <Documentation 
              {...commonFormProps}
              eventKey="5" 
              slug={caseRecord.slug} 
            />
          )}
          <AuthLetters
            {...commonFormProps}
            showForm={true}
            eventKey="6"
            typeModule="Hospitalization"
            hideMedicField={true}
            hideComplement={true}
            hideSubstitution={true}
            {...props}
          />
          {!isEmpty(caseRecord) && (
            <Binnacle 
              {...commonFormProps}
              eventKey="7" 
              slug={caseRecord.slug} 
              eventStage="Ingreso hospitalario" 
            />
          )}
        </Accordion>
      </BlockCaseContext.Provider>

      <Row>
        <Col hidden={!buttonVisibility.showControlDesk}>
          <Button 
            className="mt-5" 
            variant="primary" 
            onClick={() => onClickUpdateCase()}
          >
            Enviar a Mesa de Control
          </Button>
        </Col>

        <Col 
          md={3} 
          className="mt-5 mr-5" 
          hidden={!buttonVisibility.showMedicInfo}
        >
          <Button 
            variant="danger" 
            onClick={() => onClickUpdateCase(CASE_STATUS.MEDIC_INFORMATION)}
          >
            Enviar a Informacion Médica Hospital
          </Button>
        </Col>
      </Row>
    </>
  );
};

export default compose(
  WithCommentValidation,
  WithMultiFormManager
)(Hospitalization);


